import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faExchangeAlt,
  faDownload,
  faMusic,
  faQuestion,
  faArrowRightArrowLeft,
  // Toolbar / side-toggle icons used inline in Step 7
  faChevronLeft, // My Project back pill
  faFont, // Fingering size button (PDF only)
  faSliders, // Side-toggle column / Pitch toggle
  faPencilAlt, // Pencil pill in the side-toggle column
  faRotate, // Reset (edit mode)
  faReply, // Undo (edit mode)
  faCircleCheck, // Done (edit mode)
} from '@fortawesome/free-solid-svg-icons';

import dragImg from '@Assets/images/tutorial/drag.png';
import swapGroupImg from '@Assets/images/tutorial/swap_group.png';
import swapOneImg from '@Assets/images/tutorial/swap_one.png';
import altFingeringOffImg from '@Assets/images/tutorial/altFg_toggle_off.png';
import altFingeringOnImg from '@Assets/images/tutorial/altFg_toggle_on.png';
import pitchSharpFlatOffImg from '@Assets/images/tutorial/pitch_sharpflat_toggle_off.png';
import sharpFlatOnImg from '@Assets/images/tutorial/sharpflat_toggle_on.png';
import pitchOnImg from '@Assets/images/tutorial/pitch_toggle_on.png';
import fontSizeSmallImg from '@Assets/images/tutorial/fontsize_small.png';
import fontSizeMiddleImg from '@Assets/images/tutorial/fontsize_middle.png';
import fontSizeLargeImg from '@Assets/images/tutorial/fontsize_large.png';
import createNewProjectImg from '@Assets/images/tutorial/create_new_project.png';
import fingeringIcon from '@Assets/images/fingering.svg';

import { DocsLayout } from '../components/docs/DocsLayout';
import type { DocsTocLink } from '../components/docs/DocsTableOfContents';
import {
  Callout,
  DocH1,
  DocH2,
  DocH3,
  DocHr,
  DocMediaFrame,
  DocP,
  DocSubtitle,
  DocUl,
  fontHeading,
  linkClass,
} from '../components/docs/docsTypography';
import { FaqAccordion, type FaqItem } from '../components/FaqAccordion';
import { usePageMeta } from '../hooks/usePageMeta';
import { getPublicPageMeta } from '../seo/publicPageMeta';
import { trackPageView } from '../services/telemetry';

/** Tallest a still may render. Images also never exceed their own pixel size. */
const TOUR_STILL_MAX_HEIGHT = 384;

/** One rendered height for the swap stills and the toolbar / side-toggle stills. */
const TOUR_MATCH_HEIGHT = 136;

/** Intrinsic pixel size of each project-page still. Images never render larger than this. */
const TOUR_STILL_PX: Record<string, { width: number; height: number }> = {
  [dragImg]: { width: 523, height: 741 },
  [swapGroupImg]: { width: 523, height: 741 },
  [swapOneImg]: { width: 523, height: 741 },
  [altFingeringOffImg]: { width: 421, height: 288 },
  [altFingeringOnImg]: { width: 421, height: 288 },
  [pitchSharpFlatOffImg]: { width: 591, height: 309 },
  [sharpFlatOnImg]: { width: 591, height: 309 },
  [pitchOnImg]: { width: 591, height: 309 },
  [fontSizeSmallImg]: { width: 421, height: 288 },
  [fontSizeMiddleImg]: { width: 421, height: 288 },
  [fontSizeLargeImg]: { width: 421, height: 288 },
};

const TOC_LINKS: readonly DocsTocLink[] = [
  { href: '#sign-up', label: 'Sign up or sign in' },
  { href: '#workspace', label: 'Go to your workspace' },
  { href: '#upload', label: 'Upload sheet music' },
  { href: '#ai-generates', label: 'AI generates fingerings' },
  { href: '#edit-mode', label: 'Enter edit mode & edit fingerings' },
  { href: '#swap-alts', label: 'Swap alternative fingerings' },
  { href: '#toolbar-side-toggles', label: 'Toolbar & side toggles' },
  { href: '#manage-projects', label: 'Manage your projects' },
  { href: '#faq', label: 'Frequently Asked Questions' },
];

// Typography primitives, TOC nav, and the two-column shell now live
// under `src/components/docs/` — see the imports above.

/**
 * JSON-LD structured data — combines a `HowTo` (per-step walkthrough,
 * unlocks Google's HowTo rich result) with a `BreadcrumbList` (gives
 * SERP breadcrumbs of `Home > Tutorial`). The FAQ accordion at the
 * bottom emits its own separate `FAQPage` JSON-LD via `FaqAccordion`,
 * which we don't duplicate here.
 */
const STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'HowTo',
      '@id': 'https://pineapplemusiclab.com/tutorial#howto',
      name: 'How to Generate AI Piano Fingerings with PineappleMusicLab',
      description:
        'A step-by-step tutorial on how to use PineappleMusicLab to upload piano sheet music, generate AI-powered fingering suggestions, edit and customize them, and export your annotated score.',
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Sign Up or Sign In',
          text: 'Create an account with "Log In" in the navigation bar, or start from the upload box on the home page without an account.',
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'Go to Your Workspace',
          text: 'After you sign in you land on your workspace. "My Projects" in the navigation bar opens it again later.',
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Upload Your Sheet Music',
          text: 'On your workspace, under Create New Project, choose Select Score to Upload (or Upload an image, PDF or MusicXML on a phone). Accepted files are MusicXML (.xml, .musicxml, .mxl), PDF, and images (JPG, PNG, and WEBP). Maximum file size is 10 MB.',
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'AI Generates Fingerings',
          text: 'The AI automatically analyzes your sheet music and generates fingering suggestions in seconds. Main fingerings are shown in bold, with lighter alternative suggestions.',
        },
        {
          '@type': 'HowToStep',
          position: 5,
          name: 'Enter Edit Mode and Change Main Fingerings',
          text: 'Tap the pencil pill in the right-edge side-toggle column to enter edit mode, then click and drag any main fingering number up or down to change it. Fingering numbers range from 1 (thumb) to 5 (pinky).',
        },
        {
          '@type': 'HowToStep',
          position: 6,
          name: 'Swap Alternative Fingerings',
          text: 'Still in edit mode, quick-click a lighter-colored alternative fingering to swap an entire connected group with its main, or long-press to swap only that individual note.',
        },
        {
          '@type': 'HowToStep',
          position: 7,
          name: 'Use the Toolbar and Side Toggles',
          text: 'PDF projects have a Fingering size slider, a PDF button for a printable PDF of the score as you see it, and an XML button. The XML menu offers MusicXML (no fingering numbers, including digital PDFs), MusicXML with Fingerings, and MusicXML with Fingerings (Sibelius). MusicXML projects engrave the score and have a size slider for fingerings and pitch letters, a PDF button, an XML button with MusicXML with Fingerings and MusicXML with Fingerings (Sibelius), and a MIDI button. Side toggles are Sharps & Flats, Pitch, Piano fingering, Alternate fingerings, and the pencil; MusicXML projects add Playback. While editing, Reset, Undo, and Done appear in the toolbar (the bottom bar on a phone). The question mark replays the project tutorial.',
        },
        {
          '@type': 'HowToStep',
          position: 8,
          name: 'Manage Your Projects',
          text: 'Visit My Projects to open, rename, delete, or create new projects at any time.',
        },
      ],
    },
    {
      '@type': 'BreadcrumbList',
      '@id': 'https://pineapplemusiclab.com/tutorial#breadcrumbs',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://pineapplemusiclab.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Tutorial',
          item: 'https://pineapplemusiclab.com/tutorial',
        },
      ],
    },
  ],
};

const TUTORIAL_FAQ_ITEMS: FaqItem[] = [
  {
    question: 'What do piano fingering numbers mean?',
    answer:
      'Piano fingering uses numbers 1–5 for each hand: 1 = thumb, 2 = index finger, 3 = middle finger, 4 = ring finger, 5 = pinky. These numbers appear above the staff (right hand) or below the staff (left hand) next to the notes on the score.',
  },
  {
    question: 'How many projects can I have on the free plan?',
    answer: (
      <>
        The free plan allows up to 3 projects with limited generation (18
        measures for MusicXML, 3 systems for PDF or page images). To generate
        full-length pieces and keep more projects, check out our{' '}
        <Link to="/pricing" className={linkClass}>
          subscription plans
        </Link>
        .
      </>
    ),
    plainTextAnswer:
      'The free plan allows up to 3 projects with limited generation (18 measures for MusicXML, 3 systems for PDF or page images). To generate full-length pieces and keep more projects, check out our subscription plans.',
  },
  {
    question: 'Are my changes saved automatically?',
    answer: (
      <>
        Yes. When you click <strong>Done</strong> in the edit-mode toolbar (or
        tap the side-toggle <strong>pencil pill</strong> a second time to exit
        edit mode), your edits are saved to the server automatically. Within an
        edit session you can use <strong>Undo</strong> to step back one change
        at a time, or <strong>Reset</strong> to revert everything you have done
        since entering edit mode.
      </>
    ),
    plainTextAnswer:
      'Yes. When you click Done in the edit-mode toolbar (or tap the side-toggle pencil pill a second time to exit edit mode), your edits are saved to the server automatically. Within an edit session you can use Undo to step back one change at a time, or Reset to revert everything you have done since entering edit mode.',
  },
  {
    question: 'Can I print or export my annotated sheet music?',
    answer: (
      <>
        Yes. PDF projects have a <strong>PDF</strong> button that downloads a
        printable PDF of the score as you see it, and an <strong>XML</strong>{' '}
        button. That menu offers <strong>MusicXML</strong> (the score without
        fingering numbers, including digital PDFs),{' '}
        <strong>MusicXML with Fingerings</strong>, and{' '}
        <strong>MusicXML with Fingerings (Sibelius)</strong>. MusicXML projects
        have a <strong>PDF</strong> button for an engraved PDF, an XML button
        with <strong>MusicXML with Fingerings</strong> and{' '}
        <strong>MusicXML with Fingerings (Sibelius)</strong>, and a{' '}
        <strong>MIDI</strong> button once the score has loaded.
      </>
    ),
    plainTextAnswer:
      'Yes. PDF projects have a PDF button that downloads a printable PDF of the score as you see it, and an XML button. That menu offers MusicXML (the score without fingering numbers, including digital PDFs), MusicXML with Fingerings, and MusicXML with Fingerings (Sibelius). MusicXML projects have a PDF button for an engraved PDF, an XML button with MusicXML with Fingerings and MusicXML with Fingerings (Sibelius), and a MIDI button once the score has loaded.',
  },
  {
    question: 'What kind of PDF works best?',
    answer:
      "Digitally exported PDFs (from notation software or a publisher's digital edition) produce the best results. High-quality scanned sheet music also works well. Photos are accepted too: a full, evenly lit page works best. Skewed angles, dim lighting, and low resolution can reduce accuracy.",
  },
];

/** Same stills the project-page tour uses. Several frames sit in a row
 *  with an arrow between them, and wrap to a column on a narrow screen. */
function TourStillPicture({
  src,
  alt,
  cropTop,
  matchHeight,
  row,
  maxHeight,
}: {
  src: string;
  alt: string;
  cropTop?: number;
  matchHeight: boolean;
  row: boolean;
  maxHeight?: number;
}) {
  const px = TOUR_STILL_PX[src];
  if (matchHeight && cropTop && px) {
    const drawnHeight = (TOUR_MATCH_HEIGHT * px.height) / (px.height - cropTop);
    return (
      <div className="overflow-hidden" style={{ height: TOUR_MATCH_HEIGHT }}>
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="block w-auto max-w-none"
          style={{
            height: drawnHeight,
            marginTop: -(cropTop / px.height) * drawnHeight,
          }}
        />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      width={px?.width}
      height={px?.height}
      draggable={false}
      className={
        matchHeight || maxHeight != null
          ? 'block w-auto max-w-full object-contain'
          : row
            ? 'block h-auto w-full min-w-0 object-contain'
            : 'block h-auto w-auto max-w-full object-contain'
      }
      style={
        matchHeight
          ? { height: TOUR_MATCH_HEIGHT, width: 'auto' }
          : px
            ? {
                maxHeight: Math.min(
                  px.height,
                  maxHeight ?? TOUR_STILL_MAX_HEIGHT
                ),
              }
            : { maxHeight: maxHeight ?? TOUR_STILL_MAX_HEIGHT }
      }
    />
  );
}

function TourStills({
  caption,
  frames,
  matchHeight = false,
  maxHeight,
}: {
  caption?: string;
  frames: Array<{ src: string; alt: string; cropTop?: number }>;
  /** Render every frame at TOUR_MATCH_HEIGHT. Swap crops stay tutorial-only. */
  matchHeight?: boolean;
  /** Cap each frame below its usual size and keep the border snug. */
  maxHeight?: number;
}) {
  const row = frames.length > 1;
  const snug = matchHeight || maxHeight != null;
  const hug = !row || snug;
  return (
    <DocMediaFrame className={hug ? 'mx-auto w-fit max-w-full' : ''}>
      <div
        className={
          row && !snug ? 'flex flex-col items-center gap-3 px-4 py-4' : 'p-2'
        }
      >
        {caption && (
          <p className="text-sm font-medium text-stone-500">{caption}</p>
        )}
        <div
          className={
            row
              ? snug
                ? `
                  flex min-w-0 flex-col items-center gap-3
                  md:flex-row md:justify-center
                `
                : `
                  flex w-full min-w-0 flex-col items-center gap-3
                  md:flex-row md:justify-center
                `
              : 'flex justify-center'
          }
        >
          {frames.map((frame, i) => {
            const px = TOUR_STILL_PX[frame.src];
            return (
              <div
                key={`${frame.src}-${i}`}
                className={
                  snug
                    ? `
                      flex w-auto max-w-full min-w-0 flex-col items-center gap-3
                      md:flex-row md:gap-2
                    `
                    : row
                      ? `
                        flex w-full min-w-0 flex-col items-center gap-3
                        md:w-auto md:flex-1 md:basis-44 md:flex-row md:gap-2
                      `
                      : 'max-w-full'
                }
                style={snug ? undefined : { maxWidth: px?.width }}
              >
                {i > 0 && (
                  <>
                    <span className="md:hidden">
                      <FontAwesomeIcon
                        icon={faArrowRightArrowLeft}
                        rotation={90}
                        className="text-[1.75rem] text-stone-400"
                      />
                    </span>
                    <span className="hidden shrink-0 md:inline-flex">
                      <FontAwesomeIcon
                        icon={faArrowRightArrowLeft}
                        className="w-7 text-[1.75rem] text-stone-400"
                      />
                    </span>
                  </>
                )}
                {i === 0 && row && (
                  <span className="hidden w-7 shrink-0 md:block" aria-hidden />
                )}
                <TourStillPicture
                  src={frame.src}
                  alt={frame.alt}
                  cropTop={frame.cropTop}
                  matchHeight={matchHeight}
                  row={row}
                  maxHeight={maxHeight}
                />
              </div>
            );
          })}
        </div>
      </div>
    </DocMediaFrame>
  );
}

function Tutorial() {
  // SEO meta tags. `ogType: 'article'` is important here — this is a
  // long-form tutorial, not a generic landing page, so social previews
  // and SEO tooling should treat it as such. The `articleModifiedTime`
  // also feeds Google's freshness signal.
  usePageMeta(getPublicPageMeta('/tutorial'));

  useEffect(() => {
    trackPageView('Tutorial', '/tutorial');
  }, []);

  return (
    <>
      {/* JSON-LD Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(STRUCTURED_DATA) }}
      />

      <DocsLayout
        tocLinks={TOC_LINKS}
        header={
          <>
            <DocH1>Getting Started with AI Piano Fingering</DocH1>
            <DocSubtitle>
              Follow this step-by-step guide to generate and customize AI piano
              fingerings for your sheet music in minutes.
            </DocSubtitle>
          </>
        }
      >
        {/* The mobile-only TOC, the wrapping <article> shell, and the
            footer all live inside `DocsLayout` — Tutorial only needs
            to provide the body content here. */}

        {/* ───────── Step 1: Sign Up or Sign In ───────── */}
        <section aria-labelledby="sign-up">
          <DocH2 id="sign-up">Sign Up or Sign In</DocH2>
          <DocP>
            Click <strong>Log In</strong> in the navigation bar to create a new
            account or sign in. You can also start from the upload box on the{' '}
            <Link to="/" className={linkClass}>
              home page
            </Link>{' '}
            without an account. Creating an account is completely free — no
            credit card required.
          </DocP>

          <DocMediaFrame>
            <img
              className="block w-full"
              src="/tutorial/01.png"
              alt="Clicking 'Get Started for Free!' on the home page or 'Log In' in the nav bar, showing the login screen."
              loading="lazy"
            />
          </DocMediaFrame>

          <Callout>
            <span className="font-medium">Tip:</span> The Free plan lets you
            keep up to 3 projects with limited generation. You can upgrade
            anytime on the{' '}
            <Link to="/pricing" className={linkClass}>
              Pricing page
            </Link>{' '}
            for more credits and full-length generation.
          </Callout>
        </section>

        <DocHr />

        {/* ───────── Step 2: Go to Your Workspace ───────── */}
        <section aria-labelledby="workspace">
          <DocH2 id="workspace">Go to Your Workspace</DocH2>
          <DocP>
            Once you are signed in, you land on your <strong>Workspace</strong>{' '}
            — the central hub where all your projects are listed.{' '}
            <strong>My Projects</strong> in the navigation bar opens it again
            later.
          </DocP>
        </section>

        <DocHr />

        {/* ───────── Step 3: Upload Your Sheet Music ───────── */}
        <section aria-labelledby="upload">
          <DocH2 id="upload">Upload Your Sheet Music</DocH2>
          <DocP>
            In your Workspace, under <strong>Create New Project</strong>, click{' '}
            <strong>Select Score to Upload</strong>. On a phone the button reads{' '}
            <strong>Upload an image, PDF or MusicXML</strong>. The upload and AI
            generation start automatically.
          </DocP>

          <div className="mx-auto max-w-sm">
            <DocMediaFrame>
              <img
                className="block h-auto w-full"
                src={createNewProjectImg}
                width={1371}
                height={1173}
                alt="Create New Project upload box with Select Score to Upload"
                draggable={false}
              />
            </DocMediaFrame>
          </div>

          <DocH3>Supported file formats</DocH3>
          <DocUl>
            <li>
              <strong>PDF</strong> — standard PDF sheet music files. The AI
              analyzes the printed score and overlays fingering numbers directly
              on the page.
            </li>
            <li>
              <strong>MusicXML</strong> — files with <code>.xml</code>,{' '}
              <code>.musicxml</code>, or <code>.mxl</code> extensions. MusicXML
              offers the highest accuracy for fingering generation.
            </li>
            <li>
              <strong>Images</strong> — JPG, PNG, or WEBP page photos.
            </li>
          </DocUl>
          <DocP>
            Maximum file size: <strong>10 MB</strong>.
          </DocP>

          <Callout>
            <span className="font-medium">MusicXML tip:</span> You can export
            MusicXML from most notation software like MuseScore, Finale,
            Sibelius, or Flat.io.
          </Callout>

          <Callout>
            <span className="font-medium">PDF tip:</span> For the best results,
            use digitally exported PDFs (e.g. from MuseScore, Finale, or a
            publisher&apos;s digital edition) or high-quality scanned sheet
            music. Photos are accepted too: a full, evenly lit page works best.
            Skewed angles, dim lighting, and low resolution can reduce accuracy.
          </Callout>
        </section>

        <DocHr />

        {/* ───────── Step 4: AI Generates Fingerings ───────── */}
        <section aria-labelledby="ai-generates">
          <DocH2 id="ai-generates">AI Generates Fingerings</DocH2>
          <DocP>
            After uploading, our AI automatically starts analyzing your piano
            sheet music. You&apos;ll see a loading spinner while it processes.
            This usually takes just a few seconds. Once complete, the score will
            appear with fingering numbers overlaid on each note.
          </DocP>

          <TourStills
            maxHeight={150}
            frames={[
              {
                src: altFingeringOffImg,
                alt: 'Score with main piano fingerings shown',
              },
              {
                src: altFingeringOnImg,
                alt: 'Score with alternate fingerings shown in lighter blue',
              },
            ]}
          />

          <DocH3>Understanding the fingering numbers</DocH3>
          <DocUl>
            <li>
              <strong>Main fingerings (bold / dark):</strong> the AI&apos;s
              primary recommendation. Shown in black on MusicXML projects, and
              dark blue on PDF projects.
            </li>
            <li>
              <strong>Alternative fingerings (light):</strong> secondary
              suggestions shown in grey on MusicXML projects, and light blue on
              PDF projects. In edit mode, tap one to swap the connected group
              with its main fingering, or hold it to swap only that note.
            </li>
          </DocUl>
        </section>

        <DocHr />

        {/* ───────── Step 5: Enter Edit Mode ───────── */}
        <section aria-labelledby="edit-mode">
          <DocH2 id="edit-mode">
            Enter Edit Mode and Change Main Fingerings
          </DocH2>
          <DocP>
            The project page opens in a read-only view by default so the score
            is safe to browse. To start changing fingerings, first tap the{' '}
            <strong>
              <FontAwesomeIcon icon={faPencilAlt} className="mx-1" />
              pencil pill
            </strong>{' '}
            in the right-edge side-toggle column. It sits just below the{' '}
            <strong>Alternate fingerings</strong> pill. Both that pill and the
            pencil appear only while <strong>Piano fingering</strong> is on. The
            pill fills in to show that edit mode is active.
          </DocP>

          <DocP>
            Once you are in edit mode,{' '}
            <strong>click and drag any main fingering number up or down</strong>{' '}
            to change it. A magnified number with small up / down hint triangles
            will follow your cursor as you drag, so you can see the new value
            before releasing. Fingering numbers range from 1 (thumb) to 5
            (pinky).
          </DocP>

          <TourStills
            maxHeight={200}
            frames={[
              {
                src: dragImg,
                alt: 'Drag a main fingering number up or down to change it',
              },
            ]}
          />

          <Callout>
            <span className="font-medium">Tip:</span> For chords with multiple
            notes, each note has its own fingering that can be edited
            independently. Drag up or down to cycle through finger numbers
            (1–5).
          </Callout>

          <Callout>
            <span className="font-medium">Read-only outside edit mode:</span>{' '}
            Dragging and swapping only work while the pencil pill is active. Tap
            it again (or use{' '}
            <strong>
              <FontAwesomeIcon icon={faCircleCheck} className="mx-1" />
              Done
            </strong>{' '}
            in the toolbar) to exit edit mode — your changes are saved
            automatically.
          </Callout>
        </section>

        <DocHr />

        {/* ───────── Step 6: Swap Alternative Fingerings ───────── */}
        <section aria-labelledby="swap-alts">
          <DocH2 id="swap-alts">Swap Alternative Fingerings</DocH2>
          <DocP>
            Many notes have alternative fingering suggestions shown in a lighter
            color. They share the same edit mode as main fingerings — make sure
            the side-toggle{' '}
            <strong>
              <FontAwesomeIcon icon={faPencilAlt} className="mx-1" />
              pencil pill
            </strong>{' '}
            is active before tapping them. Once in edit mode, you can swap an
            alternative with its main fingering two ways:
          </DocP>

          <DocH3>Quick click — group swap</DocH3>
          <DocP>
            Tap or quick-click an alternative fingering number to swap it and
            all connected alternative fingerings with their main fingerings.
            This is useful for passages where an entire phrase should use the
            alternative fingering pattern.
          </DocP>

          <TourStills
            matchHeight
            frames={[
              {
                src: swapGroupImg,
                alt: 'Tap an alternative to swap the connected group',
                cropTop: 248,
              },
            ]}
          />

          <DocH3>Long press — individual swap</DocH3>
          <DocP>
            Press and hold an alternative fingering for about 250ms to swap only
            that individual note&apos;s fingering. This lets you mix and match
            main and alternative fingerings within a phrase.
          </DocP>

          <TourStills
            matchHeight
            frames={[
              {
                src: swapOneImg,
                cropTop: 248,
                alt: 'Hold an alternative to swap only that note',
              },
            ]}
          />
        </section>

        <DocHr />

        {/* ───────── Step 7: Toolbar & Side Toggles ───────── */}
        <section aria-labelledby="toolbar-side-toggles">
          <DocH2 id="toolbar-side-toggles">Toolbar &amp; Side Toggles</DocH2>
          <DocP>
            Every project page has two control surfaces: a sticky{' '}
            <strong>top toolbar</strong> with project-level actions, and a
            floating <strong>side-toggle column</strong> pinned to the right
            edge of the score for view toggles and the edit-mode pencil. On a
            phone those controls sit in the bottom bar. While editing, Reset,
            Undo, and Done appear in the toolbar.
          </DocP>

          <DocH3>Top toolbar (read-only mode)</DocH3>
          <DocUl>
            <li>
              <FontAwesomeIcon
                icon={faChevronLeft}
                className="mr-1 text-stone-500"
              />
              <strong>My Project:</strong> back to your workspace. Fades to
              non-interactive while in edit mode so you can&apos;t accidentally
              bail mid-edit.
            </li>
            <li>
              <FontAwesomeIcon icon={faFont} className="mr-1 text-stone-500" />
              <strong>Fingering size:</strong> opens a small popover with a
              slider to scale fingering numbers between small, medium, and
              large. MusicXML projects also have a slider for pitch letters.
              <TourStills
                matchHeight
                frames={[
                  { src: fontSizeSmallImg, alt: 'Small fingering numbers' },
                  { src: fontSizeMiddleImg, alt: 'Medium fingering numbers' },
                  { src: fontSizeLargeImg, alt: 'Large fingering numbers' },
                ]}
              />
            </li>
            <li>
              <FontAwesomeIcon icon={faPlay} className="mr-1 text-stone-500" />
              <strong>Playback</strong>{' '}
              <span className="text-stone-400">(MusicXML)</span>: plays the
              engraved score. Tap a note to move the cursor, then Play.
            </li>
            <li>
              <FontAwesomeIcon
                icon={faDownload}
                className="mr-1 text-stone-500"
              />
              <strong>PDF:</strong> downloads a printable PDF of the score as
              you see it. On MusicXML projects this is the engraved score.
            </li>
            <li>
              <FontAwesomeIcon
                icon={faDownload}
                className="mr-1 text-stone-500"
              />
              <strong>XML:</strong> opens a menu. PDF projects offer{' '}
              <strong>MusicXML</strong> (the score without fingering numbers,
              including digital PDFs), <strong>MusicXML with Fingerings</strong>
              , and <strong>MusicXML with Fingerings (Sibelius)</strong>.
              MusicXML projects offer only the two fingered files.
            </li>
            <li>
              <FontAwesomeIcon
                icon={faDownload}
                className="mr-1 text-stone-500"
              />
              <strong>MIDI</strong>{' '}
              <span className="text-stone-400">(MusicXML)</span>: downloads a
              MIDI file of the engraved score once it has loaded.
            </li>
            <li>
              <FontAwesomeIcon
                icon={faQuestion}
                className="mr-1 text-stone-500"
              />
              <strong>?:</strong> tap the question mark anytime to replay the
              project tutorial and revisit what each control does.
            </li>
          </DocUl>

          <DocH3>Side-toggle column (right edge)</DocH3>
          <DocUl>
            <li>
              <FontAwesomeIcon icon={faMusic} className="mr-1 text-stone-500" />
              <strong>Sharps &amp; Flats:</strong> highlights notes affected by
              sharps and flats with a different notehead color.
              <TourStills
                matchHeight
                frames={[
                  {
                    src: pitchSharpFlatOffImg,
                    alt: 'Score before sharps and flats are highlighted',
                  },
                  {
                    src: sharpFlatOnImg,
                    alt: 'Noteheads affected by sharps and flats highlighted in color',
                  },
                ]}
              />
            </li>
            <li>
              <img
                src={fingeringIcon}
                alt=""
                draggable={false}
                className="mr-1 inline-block h-5 w-auto align-text-bottom"
              />
              <strong>Piano fingering:</strong> shows or hides the fingering
              numbers. Turn this off to see the score without them. Alternate
              fingerings and the pencil appear only while this is on.
              <TourStills
                matchHeight
                frames={[
                  {
                    src: altFingeringOffImg,
                    alt: 'Score with piano fingerings shown',
                  },
                ]}
              />
            </li>
            <li>
              <FontAwesomeIcon
                icon={faExchangeAlt}
                className="mr-1 text-stone-500"
              />
              <strong>Alternate fingerings:</strong> shows the suggested
              alternatives next to the main numbers — lighter blue on PDF
              projects, grey on MusicXML projects. The pencil appears only while
              this is on.
              <TourStills
                matchHeight
                frames={[
                  {
                    src: altFingeringOffImg,
                    alt: 'Score with alternate fingerings hidden',
                  },
                  {
                    src: altFingeringOnImg,
                    alt: 'Score with alternate fingerings shown',
                  },
                ]}
              />
            </li>
            <li>
              <FontAwesomeIcon
                icon={faPencilAlt}
                className="mr-1 text-stone-500"
              />
              <strong>Edit fingerings (pencil):</strong> enters fingering edit
              mode. While the pill is filled in, drag / swap is enabled on the
              score and the toolbar shows the Reset / Undo / Done controls
              described below.
            </li>
            <li>
              <FontAwesomeIcon
                icon={faSliders}
                className="mr-1 text-stone-500"
              />
              <strong>Pitch:</strong> PDF projects toggle note names on or off.
              MusicXML projects choose every note, only notes with two or more
              ledger lines, or none.
              <TourStills
                matchHeight
                frames={[
                  { src: pitchSharpFlatOffImg, alt: 'Score with pitch hidden' },
                  {
                    src: pitchOnImg,
                    alt: 'Score with note names shown on noteheads',
                  },
                ]}
              />
            </li>
          </DocUl>

          <DocH3>Toolbar (edit mode)</DocH3>
          <DocP>
            While editing, these controls appear in the toolbar. On a phone they
            sit in the bottom bar. Fingering size and the download buttons step
            aside until you leave edit mode:
          </DocP>
          <DocUl>
            <li>
              <FontAwesomeIcon
                icon={faRotate}
                className="mr-1 text-stone-500"
              />
              <strong>Reset:</strong> reverts every change you have made since
              entering this edit session.
            </li>
            <li>
              <FontAwesomeIcon icon={faReply} className="mr-1 text-stone-500" />
              <strong>Undo:</strong> steps back one fingering change at a time
              within the current edit session.
            </li>
            <li>
              <FontAwesomeIcon
                icon={faCircleCheck}
                className="mr-1 text-stone-500"
              />
              <strong>Done:</strong> auto-saves your edits to the server and
              exits edit mode. Tapping the pencil pill again does the same
              thing.
            </li>
          </DocUl>

          <Callout>
            <span className="font-medium">Tip:</span> Hover any side-toggle pill
            to see its label slide out — handy when you forget which icon does
            what. A tap activates the pill; the label does not stay open
            afterward.
          </Callout>
        </section>

        <DocHr />

        {/* ───────── Step 8: Manage Your Projects ───────── */}
        <section aria-labelledby="manage-projects">
          <DocH2 id="manage-projects">Manage Your Projects</DocH2>
          <DocP>
            The{' '}
            <Link to="/workspace" className={linkClass}>
              My Projects
            </Link>{' '}
            page is your hub for all your work. Here you can:
          </DocP>

          <DocUl>
            <li>
              <strong>Open a project</strong> — click on any project name to
              view and continue editing.
            </li>
            <li>
              <strong>Rename a project</strong> — use the 3-dot menu (⋮) on a
              project row and select &quot;Rename&quot;.
            </li>
            <li>
              <strong>Delete a project</strong> — use the 3-dot menu (⋮) and
              select &quot;Delete&quot;. A confirmation dialog will appear
              before deletion.
            </li>
            <li>
              <strong>Create a new project</strong> — under Create New Project,
              click Select Score to Upload.
            </li>
          </DocUl>

          <DocMediaFrame>
            <img
              className="block w-full"
              src="/tutorial/08.png"
              alt="The My Projects page showing project list with names, dates, and the 3-dot action menu with Rename and Delete options."
              loading="lazy"
            />
          </DocMediaFrame>
        </section>

        <DocHr />

        {/* ───────── Frequently Asked Questions ───────── */}
        <section aria-labelledby="faq">
          <DocH2 id="faq">Frequently Asked Questions</DocH2>
          <div className="mt-6">
            <FaqAccordion items={TUTORIAL_FAQ_ITEMS} showHeading={false} />
          </div>
        </section>

        <DocHr />

        {/* CTA */}
        <div className="text-center">
          <h2
            className={`
                    mb-3 text-xl font-bold text-stone-900
                    sm:text-2xl
                    ${fontHeading}
                  `}
          >
            Ready to get started?
          </h2>
          <p className="mb-6 text-stone-600">
            Create your free account and upload your first piece today.
          </p>
          <Link
            to="/"
            className={`
              inline-block cursor-pointer rounded-lg bg-yellow-400 px-8 py-4
              text-lg font-semibold text-gray-800 transition-transform
              duration-100
              hover:bg-yellow-500
              active:scale-95 active:bg-yellow-600
              sm:text-xl
            `}
          >
            Get Started for Free!
          </Link>
        </div>
      </DocsLayout>
    </>
  );
}

export { Tutorial };
